import './polyfills';

import TagManager from 'react-gtm-module';
import { type FC } from 'react';
import { Workbox } from 'workbox-window';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { WorkboxLifecycleEvent } from 'workbox-window/utils/WorkboxEvent';
import fetchIntercept, { FetchInterceptorResponse } from 'fetch-intercept';

import { isAdminPortal } from '@eon-home/react-library';

import { ResponseError } from '@swagger-http';

import i18n from '@src/i18n';
import packageMeta from '../package.json';

import { App } from '@components';
import { getNewToken } from '@store/actions';
import { configureStore } from '@store';
import { SettingsActionTypes } from '@store/enums';
import {
    IS_PROD,
    GOOGLE_TAG_MANAGER_ID,
    CONSENT_COOKIE,
} from '@tools/constants';
import { logOut, goToLogin, handleError, isLocalhost } from '@tools/utils';
import { initNewRelic } from '@src/newrelic';

if (!isAdminPortal() && !isLocalhost) {
    initNewRelic();
}

export const store = configureStore();

if (localStorage.getItem(CONSENT_COOKIE) === '0') {
    TagManager.initialize({
        gtmId: GOOGLE_TAG_MANAGER_ID,
    });
}

const isAuthRoute = (url: string): boolean =>
    url.includes('refresh') ||
    url.includes('sign-in') ||
    url.includes('sign-out');

let isRefreshing = false;

const node = document.getElementById('root') || document.createElement('div');
const root = createRoot(node);

fetchIntercept.register({
    // prettier-ignore
    request: (url: string, config: RequestInit): [string, RequestInit] => [url, config],
    requestError: (error: Error): Promise<Error> => Promise.reject(error),
    response: (
        response: FetchInterceptorResponse,
    ): FetchInterceptorResponse => {
        if (response.status !== 401) {
            return response;
        }

        const cleanUp = () => {
            logOut(store.dispatch);
            goToLogin();
        };

        if (isAuthRoute(response.url)) {
            cleanUp();

            return response;
        }

        if (!isRefreshing) {
            isRefreshing = true;

            getNewToken(store)
                .then(() => window.location.reload())
                .catch(cleanUp)
                .finally(() => {
                    isRefreshing = false;
                });
        }

        return response;
    },
    responseError: (error: Error): Promise<Error> => Promise.reject(error),
});

const renderRoot = (Application: FC): void => {
    root.render(
        <HelmetProvider>
            <I18nextProvider i18n={i18n}>
                <Provider store={store}>
                    <BrowserRouter>
                        <Application />
                    </BrowserRouter>
                </Provider>
            </I18nextProvider>
        </HelmetProvider>,
    );
};

if (IS_PROD) {
    if ('serviceWorker' in navigator) {
        const sw = 'service-worker.js';
        const wb = new Workbox(`/${sw}`);

        const previousVersion = localStorage.getItem('version');
        const currentVersion = packageMeta.version;

        // checking if an old version is stored in localStorage
        // - if it exists and differs from the current version, prompt the user to update
        // - if no previous version is found, assume this is the user's first visit, meaning
        //   they are already seeing the latest version without any cached resources
        const isNewRelease =
            previousVersion && previousVersion !== currentVersion;

        const onServiceWorkerUpdate = (condition?: boolean): void => {
            if (!condition || !isNewRelease) return;

            store.dispatch({
                type: SettingsActionTypes.OPEN_UPDATE_MODAL,
            });
        };

        wb.addEventListener('installed', (event: WorkboxLifecycleEvent) => {
            onServiceWorkerUpdate(
                event.isUpdate && event.sw?.scriptURL.includes(sw),
            );
        });

        wb.addEventListener('waiting', (event: WorkboxLifecycleEvent) => {
            onServiceWorkerUpdate(event.sw?.scriptURL.includes(sw));
        });

        wb.register().catch((e) => e);

        // store the version if it's missing (first visit)
        if (!previousVersion) {
            localStorage.setItem('version', currentVersion);
        }
    }
}

window.addEventListener('unhandledrejection', async (event) => {
    event.preventDefault();

    await handleError(
        new ResponseError(new Response(await event.promise)),
        `UNHANDLED PROMISE REJECTION: ${event.reason}`,
    );
});

renderRoot(App);
